/**
 * Speech Recognition Service — Browser-based speech-to-text via Web Speech API.
 *
 * Wraps the SpeechRecognition API (Chrome/Edge/Safari) with a simple
 * callback-driven interface. Entirely client-side, no server cost.
 */

export type SpeechRecognitionState =
  | "idle"
  | "listening"
  | "processing"
  | "error"
  | "unsupported";

export type SpeechRecognitionCallbacks = {
  onResult: (transcript: string, isFinal: boolean) => void;
  onStateChange: (state: SpeechRecognitionState) => void;
  onError: (error: string) => void;
};

// Web Speech API types — not in standard TS DOM lib (vendor-prefixed).
/* eslint-disable no-var */
declare global {
  interface SpeechRecognition extends EventTarget {
    continuous: boolean;
    interimResults: boolean;
    maxAlternatives: number;
    lang: string;
    onstart: ((this: SpeechRecognition, ev: Event) => void) | null;
    onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => void) | null;
    onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => void) | null;
    onend: ((this: SpeechRecognition, ev: Event) => void) | null;
    onspeechend: ((this: SpeechRecognition, ev: Event) => void) | null;
    start(): void;
    stop(): void;
    abort(): void;
  }
  interface SpeechRecognitionEvent extends Event {
    readonly resultIndex: number;
    readonly results: SpeechRecognitionResultList;
  }
  interface SpeechRecognitionResultList {
    readonly length: number;
    [index: number]: SpeechRecognitionResult;
  }
  interface SpeechRecognitionResult {
    readonly isFinal: boolean;
    readonly length: number;
    [index: number]: SpeechRecognitionAlternative;
  }
  interface SpeechRecognitionAlternative {
    readonly transcript: string;
    readonly confidence: number;
  }
  interface SpeechRecognitionErrorEvent extends Event {
    readonly error: string;
    readonly message: string;
  }
  var SpeechRecognition: { new (): SpeechRecognition } | undefined;
  var webkitSpeechRecognition: { new (): SpeechRecognition } | undefined;
}
/* eslint-enable no-var */

function getSpeechRecognitionCtor(): (new () => SpeechRecognition) | null {
  const w = window as unknown as Record<string, unknown>;
  return (
    (w.SpeechRecognition as new () => SpeechRecognition) ??
    (w.webkitSpeechRecognition as new () => SpeechRecognition) ??
    null
  );
}

export class SpeechRecognitionService {
  private recognition: SpeechRecognition | null = null;
  private callbacks: SpeechRecognitionCallbacks;
  private _state: SpeechRecognitionState = "idle";
  private _continuous = true;

  /** Check browser support before instantiating. */
  static isSupported(): boolean {
    return getSpeechRecognitionCtor() !== null;
  }

  constructor(callbacks: SpeechRecognitionCallbacks) {
    this.callbacks = callbacks;
  }

  get state(): SpeechRecognitionState {
    return this._state;
  }

  /**
   * Begin listening for speech.
   * @param continuous  true for toggle mode (keep listening), false for hold-to-talk (single utterance)
   * @param lang        BCP-47 language tag (default: browser default)
   */
  start(continuous = true, lang?: string): void {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      this.setState("unsupported");
      this.callbacks.onError("Speech recognition is not supported in this browser.");
      return;
    }

    // Tear down any existing instance
    this.abort();

    const rec = new Ctor();
    rec.continuous = continuous;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    if (lang) rec.lang = lang;

    this._continuous = continuous;
    this.recognition = rec;

    rec.onstart = () => {
      this.setState("listening");
    };

    rec.onresult = (event: SpeechRecognitionEvent) => {
      let interim = "";
      let finalTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }

      if (finalTranscript) {
        this.callbacks.onResult(finalTranscript.trim(), true);
      } else if (interim) {
        this.callbacks.onResult(interim, false);
      }
    };

    rec.onerror = (event: SpeechRecognitionErrorEvent) => {
      // "no-speech" and "aborted" are not real errors
      if (event.error === "no-speech" || event.error === "aborted") {
        return;
      }
      this.setState("error");
      const messages: Record<string, string> = {
        "not-allowed": "Microphone access denied. Check browser permissions.",
        "service-not-available": "Speech service unavailable. Try again later.",
        network: "Network error. Check your connection.",
        "audio-capture": "No microphone found.",
      };
      this.callbacks.onError(messages[event.error] ?? `Speech error: ${event.error}`);
    };

    rec.onend = () => {
      // If still in listening state, it ended naturally (e.g. silence timeout)
      if (this._state === "listening") {
        this.setState("idle");
      }
    };

    rec.onspeechend = () => {
      if (!continuous) {
        this.setState("processing");
      }
    };

    try {
      rec.start();
    } catch (err) {
      this.setState("error");
      this.callbacks.onError(
        err instanceof Error ? err.message : "Failed to start speech recognition."
      );
    }
  }

  /** Stop listening and finalize the last result. */
  stop(): void {
    if (this.recognition) {
      try {
        this.recognition.stop();
      } catch {
        // already stopped
      }
    }
    this.setState("idle");
  }

  /** Cancel without finalizing — discard any pending result. */
  abort(): void {
    if (this.recognition) {
      try {
        this.recognition.abort();
      } catch {
        // already stopped
      }
      this.recognition = null;
    }
    this.setState("idle");
  }

  private setState(state: SpeechRecognitionState): void {
    if (this._state === state) return;
    this._state = state;
    this.callbacks.onStateChange(state);
  }
}
